import { cn } from "@/lib/utils";
import { MarkdownChunk, cleanMarkdownBody } from "@/components/page";
import { NumberedSectionHeader } from "./NumberedSectionHeader";

/**
 * Glue between the editorial chrome (numbered eyebrow + title) and the
 * existing Hebrew-safe markdown renderer (`MarkdownChunk`).
 *
 * Hands the body through `cleanMarkdownBody` first so leading `#` titles
 * embedded in the markdown don't collide with the section's own title.
 *
 * Note: `MarkdownChunk` expects a SINGLE chunk (no horizontal rules
 * inside). If the caller has a body containing `---`, they should split
 * it themselves before mapping over sections — same way SessionDetailPage
 * already does.
 */
export type EditorialMarkdownSectionProps = {
  /** 1-based index for the numbered eyebrow. */
  index: number;
  /** Section title shown above the body. */
  title: string;
  /** The markdown chunk. */
  body: string;
  /** Anchor id matching the corresponding TOC entry. */
  id?: string;
  /** Optional kicker after the number, e.g. "Methodology". */
  kicker?: string;
  /** Optional small line beneath the section title. */
  subtitle?: string;
  /** Optional credit line shown after the markdown body. */
  credit?: string;
  /** Extra classes on the wrapper. */
  className?: string;
};

export function EditorialMarkdownSection({
  index,
  title,
  body,
  id,
  kicker,
  subtitle,
  credit,
  className,
}: EditorialMarkdownSectionProps) {
  const cleaned = cleanMarkdownBody(body);

  return (
    <section className={cn("scroll-mt-24", className)}>
      <NumberedSectionHeader
        index={index}
        title={title}
        kicker={kicker}
        subtitle={subtitle}
        id={id}
      />

      <div className="prose-stone max-w-none">
        <MarkdownChunk text={cleaned} />
      </div>

      {credit ? (
        <p className="mt-6 text-[11px] tracking-wide text-stone-400">
          {credit}
        </p>
      ) : null}
    </section>
  );
}
